import type { DirectusQuery } from '../types/localDb.types'

/**
 * Menyatukan bentuk query param Directus yang dipakai service MVDP:
 * objek bersarang (`filter: {...}`), string JSON, dan notasi bracket
 * (`'filter[_and][0][type][_eq]': 'supplier'`). Hasilnya objek bersarang tunggal.
 */

const BRACKET_KEY = /^([^[\]]+)((?:\[[^[\]]*\])+)$/

const isIndex = (segment: string) => /^\d+$/.test(segment)

function assignPath(target: Record<string, unknown>, path: string[], value: unknown) {
  let node: Record<string, unknown> | unknown[] = target
  path.forEach((segment, index) => {
    const last = index === path.length - 1
    const key = isIndex(segment) ? Number(segment) : segment
    const container = node as Record<string | number, unknown>
    if (last) {
      container[key] = value
      return
    }
    if (container[key] === undefined || typeof container[key] !== 'object') {
      container[key] = isIndex(path[index + 1]) ? [] : {}
    }
    node = container[key] as Record<string, unknown>
  })
}

/** Index bracket bisa melompat (mis. `[_and][3]` tanpa `[2]`); buang lubang array. */
function compact(value: unknown): unknown {
  if (Array.isArray(value)) return value.filter(item => item !== undefined).map(compact)
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, compact(child)]))
  }
  return value
}

export function normalizeQueryParams(params: Record<string, unknown> | undefined): DirectusQuery {
  const result: Record<string, unknown> = {}
  for (const [rawKey, value] of Object.entries(params ?? {})) {
    if (value === undefined) continue
    const match = BRACKET_KEY.exec(rawKey)
    if (!match) {
      result[rawKey] = value
      continue
    }
    const segments = [match[1], ...match[2].slice(1, -1).split('][')]
    const normalized = segments[segments.length - 1] === '' ? segments.slice(0, -1) : segments
    assignPath(result, normalized, value)
  }
  return compact(result) as DirectusQuery
}
